/**
 * Route guards.
 *
 * These decide *navigation*, not authorisation. A guard failing means the user
 * sees a sensible screen instead of a blank one; the server independently
 * refuses every request they are not entitled to make.
 */

import { Navigate, Outlet, useLocation } from "react-router-dom";

import type { PortalId } from "@/shared/api/types";
import { useAuth } from "@/shared/auth/hooks";
import { PORTAL_HOME } from "@/shared/navigation/nav.config";
import { Spinner } from "@/shared/ui";

function FullPageSpinner({ label }: { label: string }) {
  return (
    <div className="grid min-h-dvh place-items-center" role="status" aria-live="polite">
      <div className="flex items-center gap-3 text-sm">
        <Spinner />
        <span className="text-muted">{label}…</span>
      </div>
    </div>
  );
}

/** Blocks until the silent refresh-cookie restore has resolved. */
export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "initialising") {
    return <FullPageSpinner label="Restoring your session" />;
  }

  if (status === "anonymous") {
    // Preserve the intended destination so the user lands where they meant to.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
}

export function RedirectIfAuthenticated() {
  const { status, session } = useAuth();

  if (status === "initialising") {
    return <FullPageSpinner label="Loading" />;
  }
  if (status === "authenticated" && session) {
    return <Navigate to={PORTAL_HOME[session.portal]} replace />;
  }
  return <Outlet />;
}

/** Keeps a user out of a portal their roles do not place them in. */
export function RequirePortal({ portal }: { portal: PortalId }) {
  const { session } = useAuth();

  if (!session) return null;
  if (session.portal !== portal) {
    return <Navigate to={PORTAL_HOME[session.portal]} replace />;
  }
  return <Outlet />;
}

/** Sends "/" to whichever portal the signed-in user belongs to. */
export function PortalRedirect() {
  const { status, session } = useAuth();

  if (status === "initialising") return <FullPageSpinner label="Loading" />;
  if (status === "anonymous" || !session) return <Navigate to="/login" replace />;
  return <Navigate to={PORTAL_HOME[session.portal]} replace />;
}
